<template>
  <div class="sessaoEstatisticas">
    <header class="sessaoEstatisticasCabecalho">
      <div>
        <span>Análise das sessões ativas</span>
        <h2>Estatísticas de audiência</h2>
        <small>
          Eventos não finalizados, sessões renovadas nos últimos 2 minutos e
          não desconectadas.
        </small>
      </div>
      <label class="sessaoEstatisticasBusca">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model.trim="termoBusca"
          type="search"
          placeholder="Buscar usuário, IP ou evento"
          aria-label="Buscar nas estatísticas"
        />
      </label>
    </header>

    <div v-if="erro" class="sessaoEstatisticasEstado sessaoEstatisticasErro">
      <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
      <span>{{ erro }}</span>
      <button type="button" @click="carregarItens">Tentar novamente</button>
    </div>

    <div v-else-if="carregando && !carregado" class="sessaoEstatisticasEstado">
      <i class="fa fa-refresh fa-spin" aria-hidden="true"></i>
      <span>Calculando estatísticas</span>
    </div>

    <template v-else>
      <section class="sessaoEstatisticasMetricas" aria-label="Resumo estatístico">
        <article>
          <span class="sessaoEstatisticasIcone sessaoEstatisticasIconeVerde">
            <i class="fa fa-users" aria-hidden="true"></i>
          </span>
          <div>
            <small>Usuários ativos</small>
            <strong>{{ formatarNumero(totais.usuarios) }}</strong>
          </div>
        </article>
        <article>
          <span class="sessaoEstatisticasIcone sessaoEstatisticasIconeAzul">
            <i class="fa fa-play-circle" aria-hidden="true"></i>
          </span>
          <div>
            <small>Sessões abertas</small>
            <strong>{{ formatarNumero(totais.sessoes) }}</strong>
          </div>
        </article>
        <article>
          <span class="sessaoEstatisticasIcone sessaoEstatisticasIconeAmarelo">
            <i class="fa fa-clone" aria-hidden="true"></i>
          </span>
          <div>
            <small>Usuários com múltiplas sessões</small>
            <strong>{{ formatarNumero(totais.usuariosMultiplasSessoes) }}</strong>
          </div>
        </article>
        <article>
          <span class="sessaoEstatisticasIcone sessaoEstatisticasIconeVermelho">
            <i class="fa fa-exchange" aria-hidden="true"></i>
          </span>
          <div>
            <small>IPs compartilhados</small>
            <strong>{{ formatarNumero(totais.ipsCompartilhados) }}</strong>
          </div>
        </article>
      </section>

      <section class="sessaoEstatisticasGrade">
        <article class="sessaoEstatisticasWidget">
          <header>
            <div>
              <span>Concorrência por conta</span>
              <h3>Ranking de usuários</h3>
            </div>
            <strong>{{ usuariosFiltrados.length }}</strong>
          </header>

          <div class="sessaoEstatisticasTabelaCabecalho sessaoEstatisticasUsuarioGrade">
            <span>Usuário</span>
            <span>Eventos</span>
            <span>IPs</span>
            <span>Sessões</span>
          </div>
          <div v-if="usuariosFiltrados.length" class="sessaoEstatisticasLista">
            <div
              v-for="(usuario, indice) in usuariosFiltrados"
              :key="usuario._id"
              class="sessaoEstatisticasUsuario sessaoEstatisticasUsuarioGrade"
            >
              <div class="sessaoEstatisticasIdentidade">
                <span class="sessaoEstatisticasPosicao">{{ indice + 1 }}</span>
                <span>
                  <strong>{{ usuario.email }}</strong>
                  <small>{{ usuario.site || "Sem site" }}</small>
                </span>
              </div>
              <div class="sessaoEstatisticasDetalhe" :title="nomesEventos(usuario.eventos)">
                <strong>{{ usuario.eventos.length }}</strong>
                <small>{{ primeiroEvento(usuario.eventos) }}</small>
              </div>
              <div class="sessaoEstatisticasDetalhe" :title="usuario.ips.join(', ')">
                <strong>{{ usuario.ips.length }}</strong>
                <small>{{ usuario.ips[0] || "Não identificado" }}</small>
              </div>
              <strong class="sessaoEstatisticasTotal">{{ usuario.sessoes }}</strong>
            </div>
          </div>
          <div v-else class="sessaoEstatisticasVazio">
            <i class="fa fa-user-o" aria-hidden="true"></i>
            <span>Nenhum usuário encontrado</span>
          </div>
        </article>

        <article class="sessaoEstatisticasWidget">
          <header>
            <div>
              <span>Acesso compartilhado</span>
              <h3>IPs usados por mais de um usuário</h3>
            </div>
            <strong>{{ ipsFiltrados.length }}</strong>
          </header>

          <div v-if="ipsFiltrados.length" class="sessaoEstatisticasLista">
            <div
              v-for="ip in ipsFiltrados"
              :key="ip._id"
              class="sessaoEstatisticasIp"
            >
              <div class="sessaoEstatisticasIpCabecalho">
                <span>
                  <i class="fa fa-globe" aria-hidden="true"></i>
                  <strong>{{ ip.ip }}</strong>
                </span>
                <span>
                  <strong>{{ ip.usuarios.length }}</strong> usuários ·
                  <strong>{{ ip.sessoes }}</strong> sessões
                </span>
              </div>
              <div class="sessaoEstatisticasTags">
                <span v-for="usuario in ip.usuarios" :key="usuario._id">
                  {{ usuario.email }}
                  <small v-if="usuario.site">{{ usuario.site }}</small>
                </span>
              </div>
              <small class="sessaoEstatisticasEventos" :title="nomesEventos(ip.eventos)">
                <i class="fa fa-calendar-o" aria-hidden="true"></i>
                {{ resumoEventos(ip.eventos) }}
              </small>
            </div>
          </div>
          <div v-else class="sessaoEstatisticasVazio">
            <i class="fa fa-check-circle-o" aria-hidden="true"></i>
            <span>Nenhum IP compartilhado encontrado</span>
          </div>
        </article>
      </section>
    </template>
  </div>
</template>

<script>
import { api } from "@/services/api.js";

function totaisVazios() {
  return {
    sessoes: 0,
    usuarios: 0,
    eventos: 0,
    ips: 0,
    ipsCompartilhados: 0,
    usuariosMultiplasSessoes: 0,
  };
}

export default {
  name: "ListSessaoViewEstatisticas",
  props: {
    site: { type: String, default: "Todos" },
  },
  computed: {
    termoNormalizado() {
      return this.normalizarTexto(this.termoBusca);
    },
    usuariosFiltrados() {
      if (!this.termoNormalizado) {
        return this.usuarios;
      }

      var termo = this.termoNormalizado;

      return this.usuarios.filter((usuario) => {
        return this.normalizarTexto(
          [
            usuario.email,
            usuario.site,
            (usuario.ips || []).join(" "),
            this.nomesEventos(usuario.eventos),
          ].join(" ")
        ).includes(termo);
      });
    },
    ipsFiltrados() {
      if (!this.termoNormalizado) {
        return this.ipsCompartilhados;
      }

      var termo = this.termoNormalizado;

      return this.ipsCompartilhados.filter((ip) => {
        return this.normalizarTexto(
          [
            ip.ip,
            (ip.usuarios || [])
              .map((usuario) => usuario.email + " " + usuario.site)
              .join(" "),
            this.nomesEventos(ip.eventos),
          ].join(" ")
        ).includes(termo);
      });
    },
  },
  methods: {
    async carregarItens() {
      var requisicao = ++this.requisicaoAtual;
      var filtros = ["_limit=250"];

      if (this.site && this.site !== "Todos") {
        filtros.push("site=" + encodeURIComponent(this.site));
      }

      this.carregando = true;
      this.erro = "";

      try {
        var response = await api.get(
          "/sessaoView/estatisticas?" + filtros.join("&")
        );
        api.valid(response.data, this.$router);

        if (response.data.erro) {
          throw new Error(
            response.data.mensagem || "Não foi possível calcular as estatísticas."
          );
        }

        if (requisicao !== this.requisicaoAtual) {
          return;
        }

        this.totais = Object.assign(totaisVazios(), response.data.totais || {});
        this.usuarios = response.data.usuarios || [];
        this.ipsCompartilhados = response.data.ipsCompartilhados || [];
        this.carregado = true;
      } catch (erro) {
        if (requisicao !== this.requisicaoAtual) {
          return;
        }

        this.erro =
          erro && erro.message
            ? erro.message
            : "Não foi possível calcular as estatísticas.";
      } finally {
        if (requisicao === this.requisicaoAtual) {
          this.carregando = false;
        }
      }
    },
    formatarNumero(valor) {
      return Number(valor || 0).toLocaleString("pt-BR");
    },
    normalizarTexto(valor) {
      return (valor || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    nomesEventos(eventos) {
      return (eventos || []).map((evento) => evento.nome).join(", ");
    },
    primeiroEvento(eventos) {
      if (!eventos || !eventos.length) {
        return "Nenhum evento";
      }

      return eventos[0].nome;
    },
    resumoEventos(eventos) {
      if (!eventos || !eventos.length) {
        return "Nenhum evento identificado";
      }

      if (eventos.length === 1) {
        return eventos[0].nome;
      }

      return eventos[0].nome + " e mais " + (eventos.length - 1);
    },
  },
  mounted() {
    this.carregarItens();
  },
  data() {
    return {
      carregado: false,
      carregando: false,
      erro: "",
      ipsCompartilhados: [],
      requisicaoAtual: 0,
      termoBusca: "",
      totais: totaisVazios(),
      usuarios: [],
    };
  },
};
</script>

<style>
.sessaoEstatisticas {
  color: #293138;
}

.sessaoEstatisticasCabecalho {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #dfe3e7;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
}

.sessaoEstatisticasCabecalho > div > span,
.sessaoEstatisticasWidget > header span {
  color: #398063;
  display: block;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
}

.sessaoEstatisticasCabecalho h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 2px 0 3px;
}

.sessaoEstatisticasCabecalho small {
  color: #737d85;
  font-size: 10px;
}

.sessaoEstatisticasBusca {
  align-items: center;
  border: 1px solid #ccd3d8;
  display: flex;
  height: 34px;
  margin: 0;
  max-width: 320px;
  width: 100%;
}

.sessaoEstatisticasBusca i {
  color: #7c868e;
  margin: 0 9px;
}

.sessaoEstatisticasBusca input {
  background: transparent;
  border: 0;
  color: inherit;
  font-size: 11px;
  height: 100%;
  min-width: 0;
  outline: 0;
  padding: 0 9px 0 0;
  width: 100%;
}

.sessaoEstatisticasMetricas {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 12px 0;
}

.sessaoEstatisticasMetricas article {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dfe3e7;
  display: flex;
  gap: 11px;
  min-height: 68px;
  padding: 10px 13px;
}

.sessaoEstatisticasIcone {
  align-items: center;
  display: inline-flex;
  flex: 0 0 34px;
  height: 34px;
  justify-content: center;
}

.sessaoEstatisticasIconeVerde {
  background: #e4f4eb;
  color: #27805a;
}

.sessaoEstatisticasIconeAzul {
  background: #e7f0f7;
  color: #386f97;
}

.sessaoEstatisticasIconeAmarelo {
  background: #fff4d8;
  color: #9b7417;
}

.sessaoEstatisticasIconeVermelho {
  background: #f8e8e8;
  color: #a34848;
}

.sessaoEstatisticasMetricas small,
.sessaoEstatisticasMetricas strong {
  display: block;
}

.sessaoEstatisticasMetricas small {
  color: #707a82;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
}

.sessaoEstatisticasMetricas strong {
  font-size: 20px;
  font-weight: 600;
  margin-top: 2px;
}

.sessaoEstatisticasGrade {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1.25fr) minmax(360px, 0.75fr);
}

.sessaoEstatisticasWidget {
  background: #ffffff;
  border: 1px solid #dfe3e7;
  min-width: 0;
}

.sessaoEstatisticasWidget > header {
  align-items: flex-start;
  border-bottom: 1px solid #e3e7ea;
  display: flex;
  justify-content: space-between;
  padding: 12px 14px 10px;
}

.sessaoEstatisticasWidget h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 2px 0 0;
}

.sessaoEstatisticasWidget > header > strong {
  background: #edf0f2;
  color: #4f5962;
  font-size: 10px;
  min-width: 28px;
  padding: 3px 7px;
  text-align: center;
}

.sessaoEstatisticasTabelaCabecalho {
  background: #f3f5f6;
  border-bottom: 1px solid #dfe3e7;
  color: #69737b;
  font-size: 8px;
  font-weight: 700;
  min-height: 30px;
  text-transform: uppercase;
}

.sessaoEstatisticasUsuarioGrade {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(180px, 1.4fr) minmax(110px, 0.8fr) minmax(105px, 0.65fr) 52px;
  padding: 0 13px;
}

.sessaoEstatisticasTabelaCabecalho span:last-child {
  text-align: right;
}

.sessaoEstatisticasLista {
  max-height: 580px;
  overflow-y: auto;
}

.sessaoEstatisticasUsuario {
  border-bottom: 1px solid #edf0f2;
  min-height: 56px;
}

.sessaoEstatisticasIdentidade {
  align-items: center;
  display: flex;
  gap: 9px;
  min-width: 0;
}

.sessaoEstatisticasIdentidade > span:last-child,
.sessaoEstatisticasDetalhe {
  min-width: 0;
}

.sessaoEstatisticasIdentidade strong,
.sessaoEstatisticasIdentidade small,
.sessaoEstatisticasDetalhe strong,
.sessaoEstatisticasDetalhe small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sessaoEstatisticasIdentidade strong {
  font-size: 11px;
}

.sessaoEstatisticasIdentidade small,
.sessaoEstatisticasDetalhe small {
  color: #7a838b;
  font-size: 8px;
  margin-top: 2px;
}

.sessaoEstatisticasDetalhe strong {
  font-size: 11px;
}

.sessaoEstatisticasPosicao {
  align-items: center;
  background: #edf0f2;
  color: #52606a;
  display: inline-flex;
  flex: 0 0 24px;
  font-size: 9px;
  height: 24px;
  justify-content: center;
}

.sessaoEstatisticasTotal {
  color: #287d59;
  font-size: 14px;
  text-align: right;
}

.sessaoEstatisticasIp {
  border-bottom: 1px solid #edf0f2;
  padding: 12px 14px;
}

.sessaoEstatisticasIpCabecalho {
  align-items: center;
  display: flex;
  font-size: 9px;
  justify-content: space-between;
}

.sessaoEstatisticasIpCabecalho > span:first-child {
  align-items: center;
  display: flex;
  font-size: 11px;
  gap: 7px;
}

.sessaoEstatisticasIpCabecalho > span:last-child {
  color: #68737c;
}

.sessaoEstatisticasTags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 9px;
}

.sessaoEstatisticasTags > span {
  background: #eef2f4;
  color: #46525b;
  font-size: 8px;
  padding: 4px 6px;
}

.sessaoEstatisticasTags small {
  color: #7d878e;
  margin-left: 3px;
}

.sessaoEstatisticasEventos {
  color: #747e86;
  display: block;
  font-size: 8px;
  margin-top: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sessaoEstatisticasEventos i {
  margin-right: 4px;
}

.sessaoEstatisticasEstado,
.sessaoEstatisticasVazio {
  align-items: center;
  color: #7b858d;
  display: flex;
  flex-direction: column;
  font-size: 10px;
  gap: 8px;
  justify-content: center;
  min-height: 180px;
}

.sessaoEstatisticasEstado {
  background: #ffffff;
  border: 1px solid #dfe3e7;
}

.sessaoEstatisticasEstado i,
.sessaoEstatisticasVazio i {
  font-size: 19px;
}

.sessaoEstatisticasErro {
  color: #a14545;
}

.sessaoEstatisticasErro button {
  background: transparent;
  border: 1px solid #b96a6a;
  color: inherit;
  font-size: 9px;
  padding: 5px 9px;
}

.body-dark .sessaoEstatisticas,
.body-dark .sessaoEstatisticasBusca input {
  color: var(--dark-text-soft) !important;
}

.body-dark .sessaoEstatisticasCabecalho,
.body-dark .sessaoEstatisticasMetricas article,
.body-dark .sessaoEstatisticasWidget,
.body-dark .sessaoEstatisticasEstado {
  background: var(--dark-surface) !important;
  border-color: var(--dark-border) !important;
}

.body-dark .sessaoEstatisticasCabecalho,
.body-dark .sessaoEstatisticasWidget > header,
.body-dark .sessaoEstatisticasTabelaCabecalho,
.body-dark .sessaoEstatisticasUsuario,
.body-dark .sessaoEstatisticasIp {
  border-color: var(--dark-border-soft) !important;
}

.body-dark .sessaoEstatisticasCabecalho small,
.body-dark .sessaoEstatisticasMetricas small,
.body-dark .sessaoEstatisticasIdentidade small,
.body-dark .sessaoEstatisticasDetalhe small,
.body-dark .sessaoEstatisticasIpCabecalho > span:last-child,
.body-dark .sessaoEstatisticasEventos,
.body-dark .sessaoEstatisticasVazio {
  color: var(--dark-muted) !important;
}

.body-dark .sessaoEstatisticasBusca {
  border-color: var(--dark-border) !important;
}

.body-dark .sessaoEstatisticasTabelaCabecalho,
.body-dark .sessaoEstatisticasWidget > header > strong,
.body-dark .sessaoEstatisticasPosicao,
.body-dark .sessaoEstatisticasTags > span {
  background: var(--dark-surface-alt) !important;
  color: var(--dark-text-soft) !important;
}

@media (max-width: 1050px) {
  .sessaoEstatisticasGrade {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .sessaoEstatisticasCabecalho {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }

  .sessaoEstatisticasBusca {
    max-width: none;
  }

  .sessaoEstatisticasMetricas {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sessaoEstatisticasTabelaCabecalho {
    display: none;
  }

  .sessaoEstatisticasUsuarioGrade {
    grid-template-columns: minmax(0, 1fr) 46px;
  }

  .sessaoEstatisticasUsuario .sessaoEstatisticasDetalhe {
    display: none;
  }
}
</style>
